/**
 * ViewNotificationModal
 * Displays notification details in a float studio style theme.
 */

import type { NotificationData, NotificationType } from "@shared/types";
import { ArrowUpRight, Check, X } from "lucide-react";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { getNotificationRoute } from "../utils/get-notification-route";
import { getNotificationIcon, timeAgo } from "../utils/notification-helpers";

interface ViewNotificationModalProps {
	notification: {
		id: string;
		title: string;
		message: string;
		type: NotificationType;
		createdAt: string;
		isRead: boolean;
		data?: NotificationData | null;
	};
	onClose: () => void;
	onMarkRead: (id: string) => Promise<unknown> | undefined;
	onNavigate: (path: string) => void;
}

export function ViewNotificationModal({
	notification,
	onClose,
	onMarkRead,
	onNavigate,
}: ViewNotificationModalProps) {
	const Icon = getNotificationIcon(notification.type);
	const route = getNotificationRoute(notification.type, notification.data);

	const handleAction = async () => {
		if (!notification.isRead) {
			await onMarkRead(notification.id);
		}
		onClose();
		if (route.clickable) {
			onNavigate(route.path);
		}
	};

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click dismiss does not require keyboard trigger
		// biome-ignore lint/a11y/noStaticElementInteractions: backdrop backdrop-blur overlay
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				background:
					"color-mix(in srgb, var(--color-dashboard-shadow-ink) 40%, transparent)",
				backdropFilter: "blur(6px)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1000,
				padding: 16,
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget) {
					onClose();
				}
			}}
		>
			<FloatPanel
				elevation={3}
				padding={24}
				style={{ width: "100%", maxWidth: 440 }}
			>
				<div className="space-y-5">
					{/* Editorial Header */}
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-start",
							borderBottom: "1px solid var(--color-dashboard-border-soft)",
							paddingBottom: 14,
						}}
					>
						<div>
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									fontWeight: 600,
									textTransform: "uppercase",
									letterSpacing: "0.10em",
									color: "var(--color-dashboard-accent-dark)",
									display: "block",
									marginBottom: 2,
								}}
							>
								Notification Dispatch
							</span>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontWeight: 600,
									fontSize: 20,
									color: "var(--color-dashboard-text)",
									margin: 0,
								}}
							>
								Message Detail
							</h3>
						</div>
						<button
							type="button"
							onClick={onClose}
							style={{
								background: "none",
								border: "none",
								color: "var(--color-dashboard-muted)",
								cursor: "pointer",
								padding: 4,
							}}
						>
							<X size={18} />
						</button>
					</div>

					{/* Content Card */}
					<div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 36,
								height: 36,
								borderRadius: "50%",
								background:
									"color-mix(in srgb, var(--color-dashboard-accent) 10%, transparent)",
								color: "var(--color-dashboard-accent-dark)",
								flexShrink: 0,
							}}
						>
							<Icon size={16} />
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<h4
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: 600,
									fontSize: 14,
									color: "var(--color-dashboard-text)",
									margin: 0,
									lineHeight: 1.3,
								}}
							>
								{notification.title}
							</h4>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 13,
									color: "var(--color-dashboard-muted-dark)",
									margin: "8px 0 0 0",
									lineHeight: 1.45,
									whiteSpace: "pre-wrap",
								}}
							>
								{notification.message}
							</p>
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									color: "var(--color-dashboard-placeholder)",
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									marginTop: 12,
									display: "block",
								}}
							>
								Received {timeAgo(notification.createdAt)}
							</span>
						</div>
					</div>

					{/* Actions */}
					<div
						style={{
							display: "flex",
							gap: 12,
							justifyContent: "flex-end",
							paddingTop: 12,
							borderTop: "1px solid var(--color-dashboard-border-soft)",
						}}
					>
						{!notification.isRead && (
							<StudioButton
								variant="quiet"
								onClick={async () => {
									await onMarkRead(notification.id);
									onClose();
								}}
							>
								<Check size={14} style={{ marginRight: 6 }} />
								Dismiss
							</StudioButton>
						)}
						{route.clickable ? (
							<StudioButton variant="primary" onClick={handleAction}>
								Go to Action
								<ArrowUpRight size={14} style={{ marginLeft: 6 }} />
							</StudioButton>
						) : (
							<StudioButton variant="primary" onClick={onClose}>
								Close
							</StudioButton>
						)}
					</div>
				</div>
			</FloatPanel>
		</div>
	);
}
